import { useCallback, useMemo, useRef, useState } from "react";
import {
  MentionedAsset,
  assetMatchesQuery,
  filterMentionableEntities,
  useMentionableAssets,
} from "../../../hooks/useMentionableAssets";

// The entity type and the asset loading moved to hooks/useMentionableAssets so other asset
// searches share them; re-exported here so existing importers keep working.
export type { MentionedAsset };

// The caret position source: any object exposing selectionStart (an
// HTMLTextAreaElement, or a plain {selectionStart} from the Lexical editor).
interface CaretSource {
  selectionStart: number | null;
}

interface UseAtMentionReturn {
  mentionedAssets: MentionedAsset[];
  isPickerOpen: boolean;
  mentionQuery: string;
  filteredAssets: MentionedAsset[];
  handleInputChange: (value: string, inputEl: CaretSource | null) => string;
  registerMention: (entity: MentionedAsset) => void;
  // Textarea-only helpers for surfaces that haven't migrated to the Lexical editor
  // (e.g. ProjectChatModal): splice the mention token into/out of the string value.
  selectAsset: (asset: MentionedAsset, inputValue: string, inputEl: HTMLTextAreaElement | null) => string;
  removeMention: (assetId: string, inputValue: string) => string;
  resolveEntity: (label: string) => MentionedAsset | undefined;
  clearMentions: () => void;
  closePicker: () => void;
  restoreMentions: (assets: MentionedAsset[]) => void;
  resolveMentionsFromText: (text: string) => MentionedAsset[];
}

// Finds the active @-mention query at the cursor position.
// Returns null if cursor is not inside a mention, or the partial query string (after @).
// Spaces are allowed in the query (so multi-word asset names can be matched); the caller
// decides whether the candidate is still a live mention by checking it against the asset list.
function getMentionQueryAtCursor(text: string, cursorPos: number): string | null {
  const textBeforeCursor = text.slice(0, cursorPos);
  const atIndex = textBeforeCursor.lastIndexOf("@");
  if (atIndex === -1) return null;

  const between = textBeforeCursor.slice(atIndex + 1);
  // A newline always ends the mention — asset names never span lines.
  if (between.includes("\n")) return null;

  return between;
}

export const MENTION_TERMINATOR = "​"; // zero-width space marks the end of an @mention

export function buildAssetLabel(groupName: string | undefined, assetName: string): string {
  return groupName ? `${groupName} / ${assetName}` : assetName;
}

// Display label for any mentionable entity. Groups use their bare name (no " / "),
// so group tokens round-trip through MENTION_REGEX and resolveMentionsFromText unchanged.
export function buildMentionLabel(entity: MentionedAsset): string {
  return entity.kind === "group" ? entity.name : buildAssetLabel(entity.groupName, entity.name);
}

// The literal token embedded in message text for a mention (without the trailing space).
export function mentionToken(entity: MentionedAsset): string {
  return `@${buildMentionLabel(entity)}${MENTION_TERMINATOR}`;
}

// Matches a full mention token in message text. Callers must re-instantiate
// (`new RegExp(MENTION_REGEX.source, MENTION_REGEX.flags)`) before exec-looping —
// a /g regex is stateful.
export const MENTION_REGEX = new RegExp(`@[^@\\n${MENTION_TERMINATOR}]+${MENTION_TERMINATOR}`, "g");

const useAtMention = (): UseAtMentionReturn => {
  const [mentionedAssets, setMentionedAssets] = useState<MentionedAsset[]>([]);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");

  // Groups and assets, with images, loaded once and shared with every other asset search
  const { allEntities } = useMentionableAssets();

  const filteredAssets = useMemo<MentionedAsset[]>(
    () => filterMentionableEntities(allEntities, mentionQuery),
    [allEntities, mentionQuery]
  );

  // Latest allEntities, read inside handleInputChange without making it a dependency
  // (keeps the callback identity stable for consumers).
  const allEntitiesRef = useRef<MentionedAsset[]>([]);
  allEntitiesRef.current = allEntities;

  const handleInputChange = useCallback(
    (value: string, inputEl: CaretSource | null): string => {
      const cursorPos = inputEl?.selectionStart ?? value.length;
      const query = getMentionQueryAtCursor(value, cursorPos);
      // Keep the mention live only while the typed text still matches an asset;
      // once it matches nothing, spaces behave like normal message text.
      const stillMatches =
        query !== null &&
        allEntitiesRef.current.some((a) => assetMatchesQuery(a, query));

      if (stillMatches) {
        setMentionQuery(query);
        setIsPickerOpen(true);
      } else {
        setIsPickerOpen(false);
        setMentionQuery("");
      }

      setMentionedAssets((prev) =>
        prev.filter((asset) => value.includes(mentionToken(asset)))
      );

      return value;
    },
    []
  );

  // Records a picked entity and closes the picker. The Lexical editor splices the mention
  // node into its own document, so the hook only records the entity and closes the
  // picker; the new text value arrives through the editor's normal onChange.
  const registerMention = useCallback((entity: MentionedAsset) => {
    setMentionedAssets((prev) => {
      if (prev.some((a) => a.id === entity.id)) return prev;
      return [...prev, entity];
    });
    setIsPickerOpen(false);
    setMentionQuery("");
  }, []);

  // Splices the picked entity's token into a plain textarea value at the active
  // @-query. Only used by non-Lexical surfaces; the editor inserts pills itself.
  const selectAsset = useCallback(
    (asset: MentionedAsset, inputValue: string, inputEl: HTMLTextAreaElement | null): string => {
      const cursorPos = inputEl?.selectionStart ?? inputValue.length;
      const textBeforeCursor = inputValue.slice(0, cursorPos);
      const atIndex = textBeforeCursor.lastIndexOf("@");

      if (atIndex === -1) return inputValue;

      const beforeMention = inputValue.slice(0, atIndex);
      const afterCursor = inputValue.slice(cursorPos);
      const token = `${mentionToken(asset)} `;
      const newValue = beforeMention + token + afterCursor;

      setMentionedAssets((prev) => {
        if (prev.some((a) => a.id === asset.id)) return prev;
        return [...prev, asset];
      });

      setIsPickerOpen(false);
      setMentionQuery("");

      const cursorAfter = beforeMention.length + token.length;
      setTimeout(() => {
        if (inputEl) {
          inputEl.setSelectionRange(cursorAfter, cursorAfter);
          inputEl.focus();
        }
      }, 0);

      return newValue;
    },
    []
  );

  const removeMention = useCallback(
    (assetId: string, inputValue: string): string => {
      const asset = mentionedAssets.find((a) => a.id === assetId);
      setMentionedAssets((prev) => prev.filter((a) => a.id !== assetId));
      if (!asset) return inputValue;

      const marker = mentionToken(asset);
      // Literal substring strip (asset names may contain regex meta chars).
      // Strip-all so a doubly-picked asset leaves no orphan marker behind.
      // Prefer the trailing-space variant first to avoid orphan double-spaces.
      return inputValue.split(`${marker} `).join("").split(marker).join("");
    },
    [mentionedAssets]
  );

  // Mentioned entities first so pills restored from a draft resolve even before the
  // asset/group stores have loaded.
  const mentionedAssetsRef = useRef(mentionedAssets);
  mentionedAssetsRef.current = mentionedAssets;
  const resolveEntity = useCallback(
    (label: string): MentionedAsset | undefined =>
      [...mentionedAssetsRef.current, ...allEntitiesRef.current].find(
        (e) => buildMentionLabel(e) === label
      ),
    []
  );

  const clearMentions = useCallback(() => {
    setMentionedAssets([]);
    setIsPickerOpen(false);
    setMentionQuery("");
  }, []);

  const closePicker = useCallback(() => {
    setIsPickerOpen(false);
  }, []);

  const restoreMentions = useCallback((assets: MentionedAsset[]) => {
    setMentionedAssets(assets);
    setIsPickerOpen(false);
    setMentionQuery("");
  }, []);

  // Reconstruct the mentioned assets from @-mention tokens already present in a text body.
  // Used to restore chips when loading a saved schedule prompt (which carries the tokens but
  // not the structured asset data). Returns whichever loaded assets are referenced in the text.
  const resolveMentionsFromText = useCallback(
    (text: string): MentionedAsset[] =>
      allEntities.filter((a) => text.includes(mentionToken(a))),
    [allEntities]
  );


  return {
    mentionedAssets,
    isPickerOpen,
    mentionQuery,
    filteredAssets,
    handleInputChange,
    registerMention,
    selectAsset,
    removeMention,
    resolveEntity,
    clearMentions,
    closePicker,
    restoreMentions,
    resolveMentionsFromText,
  };
};

export default useAtMention;
